import { inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { accountLoaded, getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { requiredFeatureFlags, type AccessConstraint, type PermissionCheckMode } from '@sb/shared/access';
import { featureFlagContextReady, FeatureService } from '@sb/shared/feature';
import {
  hasAllPermissions,
  hasAllPermissionsForReports,
  hasPermission,
  type PermissionCheck,
  type PermissionOption,
  type PermissionState,
} from '@sb/shared/permission/util';
import { createRedirectUrlTree, type RedirectSource } from '@sb/shared/util';
import { ToastService } from '@sb/ui/toast';
import { SubscriptionModel } from '@shiftbase-com/ts-shared/models';
import { hasAtLeastSubscriptionPlan } from '@shiftbase-com/ts-shared/utilities';
import { combineLatest, map, Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

/** Where a denied feature-flag / plan requirement sends the user when the constraint sets no explicit redirect. */
export const DEFAULT_ACCESS_DENIED_REDIRECT = '/dashboard/my-overview';

/**
 * Evaluates an {@link AccessConstraint} against the current permission state,
 * subscription and feature flags, producing a route-guard outcome. It is the
 * redirect/toast-aware mirror of the pure `canAccess` evaluator in
 * `@sb/shared/access`: both decide access from the same three dimensions, but this
 * additionally waits for the data it needs and turns a denial into a `UrlTree` or a
 * cancelled navigation. Shared by the route `accessGuard`.
 */
@Injectable({ providedIn: 'root' })
export class AccessChecker {
  #store = inject<Store<AppState>>(Store);
  #featureService = inject(FeatureService);
  #router = inject(Router);
  #toast = inject(ToastService);
  #translate = inject(TranslateService);

  /**
   * Resolve access once the data it needs is loaded: `true` to allow, a `UrlTree`
   * to redirect (feature-flag / plan denial), or `false` to cancel navigation
   * (permission denial, which also raises a toast).
   *
   * Pass `route` — the snapshot being denied — to have a redirect keep the matrix
   * params, query params and fragment it was entered with; without it the redirect
   * lands on the bare target path.
   */
  check(constraint: AccessConstraint, route?: RedirectSource): Observable<boolean | UrlTree> {
    if (!this.#requiresEvaluation(constraint)) {
      return of(true);
    }

    return this.#whenReady().pipe(
      switchMap(() =>
        combineLatest([
          this.#store.select(getPermissionState).pipe(take(1)),
          this.#store.select(getAccountSubscription).pipe(take(1)),
        ]),
      ),
      map(([permissionState, subscription]) => this.#evaluate(constraint, permissionState, subscription, route)),
    );
  }

  /** Whether the constraint gates on any dimension; an empty one allows everyone. */
  #requiresEvaluation(constraint: AccessConstraint): boolean {
    return (
      requiredFeatureFlags(constraint).length > 0 ||
      constraint.permission !== undefined ||
      constraint.subscriptionPlan !== undefined
    );
  }

  #whenReady(): Observable<unknown> {
    const accountReady$ = this.#store.select(accountLoaded).pipe(
      filter((loaded) => !!loaded),
      take(1),
    );

    return combineLatest([accountReady$, featureFlagContextReady(this.#featureService)]);
  }

  // Dimensions are checked in order of how fundamental the denial is, which also
  // decides the outcome when a user fails more than one:
  //   1. feature flag  — the feature does not exist for this user at all → redirect;
  //   2. permission    — the user is not allowed here regardless of plan → cancel
  //                      navigation with a toast (redirecting to an upsell would
  //                      wrongly imply upgrading grants access);
  //   3. minimum plan  — the feature exists and is permitted, it is just gated by
  //                      the subscription → redirect (the upsell of last resort).
  #evaluate(
    constraint: AccessConstraint,
    permissionState: PermissionState,
    subscription: SubscriptionModel | null,
    route: RedirectSource | undefined,
  ): boolean | UrlTree {
    const flags = requiredFeatureFlags(constraint);
    if (flags.length > 0 && !flags.every((flag) => this.#featureService.isEnabled(flag))) {
      return this.#redirect(constraint.featureFlagRedirect, route);
    }

    if (
      constraint.permission !== undefined &&
      !this.#hasPermission(constraint.permission, constraint.permissionCheck, permissionState)
    ) {
      this.#toast.openDanger(this.#translate.instant('You do not have permission to open that page'));
      return false;
    }

    if (
      constraint.subscriptionPlan &&
      !hasAtLeastSubscriptionPlan(constraint.subscriptionPlan, subscription ?? undefined)
    ) {
      return this.#redirect(constraint.subscriptionPlanRedirect, route);
    }

    return true;
  }

  #redirect(redirectTo: string | undefined, route: RedirectSource | undefined): UrlTree {
    const target = redirectTo ?? DEFAULT_ACCESS_DENIED_REDIRECT;

    if (!route) {
      return this.#router.parseUrl(target);
    }

    return createRedirectUrlTree(this.#router, target, route);
  }

  #hasPermission(
    permission: PermissionOption,
    mode: PermissionCheckMode | undefined,
    permissionState: PermissionState,
  ): boolean {
    const check: PermissionCheck = { permissions: permission, userId: 'me', departments: 'any' };
    switch (mode) {
      case 'all':
        return hasAllPermissions(check, permissionState);
      case 'reports':
        return hasAllPermissionsForReports(check, permissionState);
      default:
        return hasPermission(check, permissionState);
    }
  }
}
